import {
  Button,
  DataCell,
  DeactivatableButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TableCell,
  TableRow,
} from '@folio/components';
import { DistributionRequestStatusBadge } from '@folio/components/features/DistributionRequestStatusBadge';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  brokerageFeeControllerGetBrokerFeesQueryOptions,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  distributionRequestStatusEnum,
  IntermediaryAdditionalInformationField,
  useDistributionRequestControllerApproveDistributionRequest,
  useDistributionRequestControllerDenyDistributionRequest,
  useDistributionRequestControllerRevokeDistributionRequest,
  useDistributionRequestControllerWaitingForSignatureDistributionRequest,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryKey,
  type DistributionRequestDto,
  type BrokerageFeeDto,
} from 'korint-sdk';
import { CheckIcon, EllipsisVerticalIcon, PauseIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IntermediaryAdditionalInfoDialogueCardModule } from './BrokerageFeeDialogueCardModule';
import { keyValueFieldsToObject } from 'korint-sdk';

interface DistributionRequestListRowProps {
  /**
   * The distribution request.
   */
  distributionRequest: DistributionRequestDto;

  /**
   * Whether the row is readonly.
   */
  isReadonly?: boolean;

  /**
   * The intermediary additional information fields to be displayed in the intermediary additional information dialogue card.
   */
  intermediaryAdditionalInformationFields: IntermediaryAdditionalInformationField[];
}

function getValidationRules(
  brokerageFirmCustomFields: Record<string, string>,
  brokerageFee: BrokerageFeeDto | undefined,
  requiredFields: IntermediaryAdditionalInformationField[],
  t: (key: string, options?: Record<string, unknown>) => string,
): Array<{ isValid: boolean; message: string }> {
  const rules: Array<{ isValid: boolean; message: string }> = [];

  for (const field of requiredFields) {
    if (!field.required) continue;

    let fieldValue: string | number | boolean | undefined;

    if (field.isCustomField) {
      fieldValue = brokerageFirmCustomFields[field.name];
    } else {
      fieldValue = brokerageFee?.[field.name as keyof BrokerageFeeDto] as
        | string
        | number
        | boolean
        | undefined;
    }

    const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === '';

    if (isEmpty) {
      const message =
        field.message ||
        t('distributionRequestList.validation.fieldRequired', {
          field: field.name,
        });
      rules.push({ isValid: false, message });
    }
  }

  return rules;
}

export function DistributionRequestListRow({
  distributionRequest,
  isReadonly,
  intermediaryAdditionalInformationFields,
}: DistributionRequestListRowProps) {
  const { data: approveOnSignature } = useSuspenseQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    select: data => data.distributionRequest?.approveOnSignature,
  });

  const queryClient = useQueryClient();

  // Sibling cards read the same endpoint with a `statuses` filter, so refetching one key leaves them stale
  const refetchDistributionRequestReaders = () =>
    queryClient.invalidateQueries({
      queryKey: viewSpecificControllerListBrokerageFirmDistributionRequestsQueryKey(
        distributionRequest.brokerageFirmId,
      ),
    });

  const { mutateAsync: approveDistributionRequest, isPending: isApprovePending } =
    useDistributionRequestControllerApproveDistributionRequest({
      mutation: {
        onSuccess: refetchDistributionRequestReaders,
      },
    });

  const {
    mutateAsync: waitingForSignatureDistributionRequest,
    isPending: isWaitingForSignaturePending,
  } = useDistributionRequestControllerWaitingForSignatureDistributionRequest({
    mutation: {
      onSuccess: refetchDistributionRequestReaders,
    },
  });

  const { mutateAsync: denyDistributionRequest, isPending: isDenyPending } =
    useDistributionRequestControllerDenyDistributionRequest({
      mutation: {
        onSuccess: refetchDistributionRequestReaders,
      },
    });

  const { mutateAsync: revokeDistributionRequest, isPending: isRevokePending } =
    useDistributionRequestControllerRevokeDistributionRequest({
      mutation: {
        onSuccess: refetchDistributionRequestReaders,
      },
    });

  const { data: fee, isLoading } = useQuery({
    ...brokerageFeeControllerGetBrokerFeesQueryOptions({
      brokerageFirmId: distributionRequest.brokerageFirmId,
    }),
    select: data => data.fees?.find(fee => fee.productId === distributionRequest.productId),
  });

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(distributionRequest.brokerageFirmId),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const hasToSendToSignature =
    approveOnSignature &&
    brokerageFirm?.firmType !== 'TEAM' &&
    (distributionRequest.status === distributionRequestStatusEnum.PENDING ||
      distributionRequest.status === distributionRequestStatusEnum.DENIED);

  const [isDeniedActionsOpen, setIsDeniedActionsOpen] = useState(false);

  const { t } = useTranslation();

  const approveOrSendToSignature = async () => {
    if (hasToSendToSignature) {
      await waitingForSignatureDistributionRequest({ id: distributionRequest.id });
    } else {
      await approveDistributionRequest({ id: distributionRequest.id });
    }
  };
  const approveLabel = hasToSendToSignature
    ? t('distributionRequestList.actions.sendForSignature')
    : t('distributionRequestList.actions.approve');

  const validationRules = getValidationRules(
    (brokerageFirm?.customFields ?? {}) as Record<string, string>,
    fee,
    intermediaryAdditionalInformationFields,
    t,
  );

  return (
    <TableRow key={distributionRequest.id}>
      <TableCell>{t(`products.${distributionRequest.productId}.label`)}</TableCell>

      <TableCell>
        <DistributionRequestStatusBadge status={distributionRequest.status} />
      </TableCell>

      <DataCell
        type="percentage"
        className="text-center"
        value={fee?.minCommission}
        defaultValue={0}
        isLoading={isLoading}
      />

      <DataCell
        type="percentage"
        className="text-center"
        value={fee?.maxCommission}
        defaultValue={100}
        isLoading={isLoading}
      />

      <DataCell
        type="price"
        className="text-center"
        value={fee?.maxManagementFee}
        defaultValue={''}
        isLoading={isLoading}
      />

      {!isReadonly && (
        <TableCell className="flex gap-2 justify-end items-center">
          {intermediaryAdditionalInformationFields.length > 0 && (
            <IntermediaryAdditionalInfoDialogueCardModule
              brokerageFirmId={distributionRequest.brokerageFirmId}
              productId={distributionRequest.productId}
              fields={intermediaryAdditionalInformationFields}
            />
          )}
          {(distributionRequest?.status === distributionRequestStatusEnum.PENDING ||
            distributionRequest?.status === distributionRequestStatusEnum.REVOKED) && (
            <DeactivatableButton
              disabled={!brokerageFirm}
              loading={isApprovePending || isWaitingForSignaturePending}
              leftIcon={<CheckIcon className="h-4 w-4 text-green-500" />}
              onClick={approveOrSendToSignature}
              variant="ghost"
              rules={validationRules}
            >
              {approveLabel}
            </DeactivatableButton>
          )}
          {distributionRequest?.status === distributionRequestStatusEnum.DENIED && (
            <Popover open={isDeniedActionsOpen} onOpenChange={setIsDeniedActionsOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('distributionRequestList.actions.more')}
                >
                  <EllipsisVerticalIcon aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-1">
                <DeactivatableButton
                  disabled={!brokerageFirm}
                  loading={isApprovePending || isWaitingForSignaturePending}
                  leftIcon={<CheckIcon className="h-4 w-4 text-green-500" />}
                  onClick={async () => {
                    await approveOrSendToSignature();
                    setIsDeniedActionsOpen(false);
                  }}
                  variant="ghost"
                  rules={validationRules}
                >
                  {approveLabel}
                </DeactivatableButton>
              </PopoverContent>
            </Popover>
          )}
          {distributionRequest?.status === distributionRequestStatusEnum.APPROVED && (
            <Button
              loading={isRevokePending}
              onClick={() => revokeDistributionRequest({ id: distributionRequest.id })}
              variant="ghost"
              leftIcon={<PauseIcon className="h-4 w-4 text-yellow-500" />}
            >
              {t('distributionRequestList.actions.revoke')}
            </Button>
          )}
          {distributionRequest?.status !== distributionRequestStatusEnum.APPROVED &&
            distributionRequest?.status !== distributionRequestStatusEnum.DENIED && (
              <DeactivatableButton
                loading={isDenyPending}
                onClick={() => denyDistributionRequest({ id: distributionRequest.id })}
                variant="ghost"
                leftIcon={<XIcon className="h-4 w-4 text-red-500" />}
                rules={validationRules}
              >
                {t('distributionRequestList.actions.deny')}
              </DeactivatableButton>
            )}
        </TableCell>
      )}
    </TableRow>
  );
}
